import { ReactNode } from 'react';
import {
  Box,
  Card,
  Divider,
  Tooltip,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  ReportLine,
  formatCount,
  formatMoney,
  formatPercent,
  percentOf
} from '@/utils/salesReport';
import { changeDirection, formatChange } from '@/utils/reportCompare';

interface ReportCardProps {
  title: string;
  icon?: ReactNode;
  /** Başlık altında görünen kısa tanım (web POS'ta aynı metin). */
  footnote?: string;
  /** Başlık yanındaki (i) ipucu. */
  hint?: string;
  action?: ReactNode;
  children: ReactNode;
}

export const ReportCard = ({
  title,
  icon,
  footnote,
  hint,
  action,
  children
}: ReportCardProps) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        p: 2.5,
        height: '100%',
        boxShadow: theme.shadows[2],
        background: theme.palette.background.paper
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {icon}
        <Typography variant="h5" fontWeight="medium" sx={{ flex: 1 }}>
          {title}
        </Typography>
        {hint && (
          <Tooltip title={hint} arrow>
            <InfoOutlined fontSize="small" color="action" />
          </Tooltip>
        )}
        {action}
      </Box>
      {footnote && (
        <Typography
          variant="caption"
          sx={{ textTransform: 'none' }}
          color="text.secondary"
          display="block"
          mt={0.5}
        >
          {footnote}
        </Typography>
      )}
      <Divider sx={{ my: 2 }} />
      {children}
    </Card>
  );
};

export const ReportEmpty = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 72,
        color: 'text.secondary'
      }}
    >
      <Typography variant="body2">{t('salesReport.empty')}</Typography>
    </Box>
  );
};

interface ReportLineRowProps {
  line: ReportLine;
  /** Verilirse yüzde + çubuk çizilir (pay / bu toplam). */
  whole?: number;
  countLabelKey?: string;
  depth?: number;
}

/** Etiket · adet · tutar (· yüzde + çubuk) satırı; alt satırlar girintili. */
export const ReportLineRow = ({
  line,
  whole,
  countLabelKey = 'salesReport.count',
  depth = 0
}: ReportLineRowProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const color = line.color ?? theme.palette.text.secondary;
  const isChild = depth > 0;
  const showBar = whole !== undefined && !isChild;

  return (
    <Box
      sx={{
        py: isChild ? 0.5 : 1,
        pl: isChild ? 3 : 0,
        ...(line.receivable && {
          border: `1px dashed ${color}`,
          borderRadius: 1,
          px: 1
        })
      }}
    >
      <Box display="flex" alignItems="baseline" gap={1}>
        {!isChild && (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: color,
              flexShrink: 0,
              alignSelf: 'center'
            }}
          />
        )}
        <Typography
          variant={isChild ? 'body2' : 'body1'}
          color={isChild ? 'text.secondary' : 'text.primary'}
          fontWeight={isChild ? 400 : 600}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {t(line.labelKey)}
        </Typography>
        {line.count !== null && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {t(countLabelKey, { n: formatCount(line.count) })}
          </Typography>
        )}
        <Typography
          variant={isChild ? 'body2' : 'body1'}
          fontWeight={isChild ? 500 : 700}
          sx={{ minWidth: 120, textAlign: 'right' }}
          noWrap
        >
          {line.total === null ? '—' : formatMoney(line.total)}
        </Typography>
        {whole !== undefined && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: 'none', minWidth: 52, textAlign: 'right' }}
          >
            {isChild ? '' : formatPercent(line.total ?? 0, whole)}
          </Typography>
        )}
      </Box>
      {line.noteKey && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ textTransform: 'none', pl: isChild ? 0 : 2.25 }}
        >
          {t(line.noteKey, line.noteParams)}
        </Typography>
      )}
      {showBar && (
        <Box
          sx={{
            mt: 0.75,
            ml: 2.25,
            height: 6,
            borderRadius: 1,
            background: alpha(color, 0.18)
          }}
        >
          <Box
            sx={{
              width: `${percentOf(line.total ?? 0, whole ?? 0)}%`,
              height: '100%',
              borderRadius: 1,
              background: color
            }}
          />
        </Box>
      )}
      {line.children?.map((child) => (
        <ReportLineRow
          key={child.key}
          line={child}
          whole={whole}
          countLabelKey={countLabelKey}
          depth={depth + 1}
        />
      ))}
    </Box>
  );
};

/** Kartın altındaki "Toplam" satırı. */
export const ReportTotalRow = ({
  label,
  count,
  total,
  countLabelKey = 'salesReport.count'
}: {
  label: string;
  count?: number;
  total: number;
  countLabelKey?: string;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Divider sx={{ my: 1 }} />
      <Box display="flex" alignItems="baseline" gap={1} py={0.5}>
        <Typography variant="body1" fontWeight={700} sx={{ flex: 1 }}>
          {label}
        </Typography>
        {count !== undefined && (
          <Typography variant="body2" color="text.secondary">
            {t(countLabelKey, { n: formatCount(count) })}
          </Typography>
        )}
        <Typography
          variant="body1"
          fontWeight={800}
          sx={{ minWidth: 120, textAlign: 'right' }}
        >
          {formatMoney(total)}
        </Typography>
      </Box>
    </>
  );
};

/** İki sütunlu küçük sayı kutusu (Kayıt / Maliyet, Kullanım / İndirim…). */
export const ReportStatPair = ({
  items
}: {
  items: { label: string; value: string; color?: string }[];
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
      gap: 2,
      mb: 2
    }}
  >
    {items.map((item) => (
      <Box key={item.label}>
        <Typography variant="body2" color="text.secondary">
          {item.label}
        </Typography>
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{ color: item.color ?? 'text.primary' }}
        >
          {item.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

interface DeltaBadgeProps {
  /** Yüzde değişim; `null` ise rozet çizilmez (önceki dönem 0). */
  change: number | null;
  /** "Önceki 7 güne göre" gibi kısa etiket. */
  label: string;
  /** Üzerine gelince: önceki aralık ve değeri. */
  details: string[];
}

/** Önceki döneme göre ▲ yeşil / ▼ kırmızı yüzde rozeti. */
export const DeltaBadge = ({ change, label, details }: DeltaBadgeProps) => {
  const theme = useTheme();
  if (change === null) return null;
  const direction = changeDirection(change);
  const color =
    direction === 'up'
      ? theme.palette.success.main
      : direction === 'down'
        ? theme.palette.error.main
        : theme.palette.text.secondary;
  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '●';

  return (
    <Tooltip
      arrow
      title={
        <Box>
          {details.map((line) => (
            <Typography key={line} variant="body2">
              {line}
            </Typography>
          ))}
        </Box>
      }
    >
      <Box
        data-testid="delta-badge"
        data-direction={direction}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          mt: 0.5,
          cursor: 'default'
        }}
      >
        <Box
          component="span"
          sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            fontWeight: 700,
            fontSize: 13,
            color,
            background: alpha(color, 0.12)
          }}
        >
          {arrow} {formatChange(change)}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};
